import PublishIcon from "@mui/icons-material/Publish";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

export const DEFAULT_PUBLISH_BATCH_SIZE = 10;

export function clampPublishBatchSize(value) {
    const n = parseInt(String(value ?? "").trim(), 10);
    if (!Number.isFinite(n)) return DEFAULT_PUBLISH_BATCH_SIZE;
    return Math.min(100, Math.max(1, n));
}

const LANGUAGE_OPTIONS = [
    { value: "", label: "Any language" },
    { value: "english", label: "English only" },
    { value: "french", label: "French only" },
];

/**
 * Batch-publish oldest REVIEWED bundles (POST …/publish).
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {(batchSize: number, language: string|null) => Promise<{ success: boolean, error?: string, data?: object }>} props.publishFn
 * @param {() => void} [props.onComplete] — refresh list after result dialog closes
 */
export default function BatchPublishReviewedDialog({ open, onClose, title, publishFn, onComplete }) {
    const [batchSizeInput, setBatchSizeInput] = useState(String(DEFAULT_PUBLISH_BATCH_SIZE));
    const [language, setLanguage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setBatchSizeInput(String(DEFAULT_PUBLISH_BATCH_SIZE));
        setLanguage("");
        setResult(null);
        setSubmitting(false);
    }, [open]);

    const handleCloseForm = useCallback(() => {
        if (!submitting) onClose();
    }, [submitting, onClose]);

    const handleSubmit = useCallback(async () => {
        const batchSize = clampPublishBatchSize(batchSizeInput);
        setBatchSizeInput(String(batchSize));
        const lang = language && String(language).trim() ? String(language).trim() : null;
        setSubmitting(true);
        const response = await publishFn(batchSize, lang);
        setSubmitting(false);
        if (!response.success) {
            setResult({ error: response.error || "Publish failed" });
            return;
        }
        setResult({ data: response.data ?? {} });
    }, [batchSizeInput, language, publishFn]);

    const handleCloseResult = useCallback(() => {
        setResult(null);
        onClose();
        onComplete?.();
    }, [onClose, onComplete]);

    const data = result?.data;
    const storyIds = Array.isArray(data?.storyIds) ? data.storyIds : [];

    return (
        <>
            <Dialog open={open && !result} onClose={handleCloseForm} maxWidth="xs" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Publishes the oldest REVIEWED bundles (up to the batch size). Leave language as
                        &quot;Any&quot; to pick across languages, or restrict to one story language.
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            autoFocus
                            label="Batch size"
                            type="number"
                            fullWidth
                            value={batchSizeInput}
                            onChange={(e) => setBatchSizeInput(e.target.value)}
                            disabled={submitting}
                            slotProps={{ htmlInput: { min: 1, max: 100 } }}
                            helperText="Number of bundles to publish (1–100). Default 10."
                        />
                        <FormControl fullWidth size="small" disabled={submitting}>
                            <InputLabel id="batch-publish-language-label">Language</InputLabel>
                            <Select
                                labelId="batch-publish-language-label"
                                label="Language"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                            >
                                {LANGUAGE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value || "any"} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseForm} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? null : <PublishIcon />}
                    >
                        {submitting ? <CircularProgress size={22} /> : "Publish"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(result)} onClose={handleCloseResult} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle>{result?.error ? "Publish failed" : "Publish result"}</DialogTitle>
                <DialogContent dividers>
                    {result?.error ? (
                        <Typography variant="body2" color="error">
                            {result.error}
                        </Typography>
                    ) : data ? (
                        <Stack spacing={1.5}>
                            <Typography variant="body2">
                                <strong>Requested batch size:</strong> {data.requestedBatchSize ?? "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Published:</strong> {data.publishedCount ?? 0}
                            </Typography>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Story IDs
                                </Typography>
                                {storyIds.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {storyIds.map((id, idx) => (
                                            <Typography
                                                component="li"
                                                variant="body2"
                                                key={`${id}-${idx}`}
                                                sx={{ fontFamily: "monospace", fontSize: "0.85rem", wordBreak: "break-all" }}
                                            >
                                                {id}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No stories were published.
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResult} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
